import { __awaiter } from '../../node_modules/tslib/tslib.es6.js';
import { isServerSideCheckoutEligible } from '../../utils/productUtils.js';
import { ConciergeProductPrice } from './ConciergeProductPrice.js';
import { Star, ChevronUp, ArrowRight, ChevronDown } from 'lucide-react';
import numeral from 'numeral';
import React__default, { useState, useMemo } from 'react';
import { Button } from '../../Components/ui/button.js';
import { Card, CardContent } from '../../Components/ui/card.js';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../../Components/ui/carousel.js';

// Sub-component for product image carousel
const ProductImageCarousel = ({ images, productTitle }) => {
    if (images.length === 0)
        return null;
    // Flexible container that handles any aspect ratio - always shows full image
    // Container adapts to image content, image uses object-contain to fit within bounds
    const imageContainerClass = 'tw:relative tw:w-full tw:flex tw:items-start tw:justify-center tw:bg-white md:tw:bg-muted tw:overflow-hidden tw:border-0 md:tw:border-b md:tw:border-r tw:border-border md:tw:pt-4';
    const imageWrapperClass = 'tw:w-full tw:flex tw:items-center tw:justify-center tw:p-3 md:tw:p-4 md:tw:pt-5 tw:min-h-[150px] tw:max-h-[450px] tw:overflow-hidden concierge-widget-image-wrapper';
    // Image scales to fit container width, height adjusts to maintain aspect ratio
    // object-contain ensures full image is visible, max-height prevents layout breakage
    const imageClass = 'tw:max-w-full tw:max-h-[400px] md:tw:max-h-[450px] tw:w-auto tw:h-auto tw:object-contain tw:block concierge-widget-image';
    if (images.length === 1) {
        return (React__default.createElement("div", { className: imageContainerClass },
            React__default.createElement("div", { className: imageWrapperClass },
                React__default.createElement("img", { src: images[0], alt: productTitle, className: imageClass, loading: "eager" }))));
    }
    return (React__default.createElement("div", { className: `${imageContainerClass} tw:group tw:min-h-[150px] concierge-widget-image-container` },
        React__default.createElement(Carousel, { opts: {
                align: 'start',
                loop: false,
            }, className: "tw:w-full" },
            React__default.createElement(CarouselContent, null, images.map((imageUrl, idx) => (React__default.createElement(CarouselItem, { key: idx },
                React__default.createElement("div", { className: imageWrapperClass },
                    React__default.createElement("img", { src: imageUrl, alt: `${productTitle} - ${idx + 1}`, className: imageClass, loading: idx === 0 ? 'eager' : 'lazy' })))))),
            images.length > 1 && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(CarouselPrevious, { className: "tw:left-2 md:tw:left-4 tw:size-7 tw:bg-background/90 tw:backdrop-blur-sm tw:border tw:shadow-md tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity" }),
                React__default.createElement(CarouselNext, { className: "tw:right-2 md:tw:right-4 tw:size-7 tw:bg-background/90 tw:backdrop-blur-sm tw:border tw:shadow-md tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity" }))))));
};
const FeaturedProductWidget = ({ product, onAddToCart, onOpenCart }) => {
    const [showDetails, setShowDetails] = useState(false);
    const [showAllRetailers, setShowAllRetailers] = useState(false);
    const [isAddingToBag, setIsAddingToBag] = useState(false);
    const images = product.imageUrls || [];
    const featureBullets = product.featureBullets || [];
    const matchedProducts = React__default.useMemo(() => product.matchedProducts || [], [product]);
    // Combine main product with matched products for multi-retailer BuyBox
    // The backend filters matchedProducts to exclude same stacklineSku, but we want to include
    // products with the same brandClubMatchingId (same product variant) at different retailers
    // If backend filtering prevents same stacklineSku, we include all matchedProducts with same brandClubMatchingId
    const allProductsToList = useMemo(() => {
        const mainProduct = product;
        const mainBrandClubMatchingId = mainProduct.brandClubMatchingId;
        const mainStacklineSku = mainProduct.stacklineSku;
        // Start with the main product
        const allProducts = [mainProduct];
        // Include matched products that share the same brandClubMatchingId (same product variant)
        // This will include the same product at different retailers, even if stacklineSku differs
        // Also include products with same stacklineSku (in case backend doesn't filter them)
        matchedProducts.forEach((matchedProduct) => {
            const hasSameMatchingId = mainBrandClubMatchingId && matchedProduct.brandClubMatchingId === mainBrandClubMatchingId;
            const hasSameStacklineSku = matchedProduct.stacklineSku === mainStacklineSku;
            // Include if same product variant (matchingId) or same stacklineSku (same product listing)
            if (hasSameMatchingId || hasSameStacklineSku) {
                allProducts.push(matchedProduct);
            }
        });
        // Remove duplicates based on stacklineSku + retailerId combination
        const seen = new Set();
        return allProducts.filter((p) => {
            if (!p || !p.retailerId)
                return false;
            const key = `${p.stacklineSku || ''}_${p.retailerId}`;
            if (seen.has(key))
                return false;
            seen.add(key);
            return true;
        });
    }, [product, matchedProducts]);
    // Show only first 3 retailers when collapsed, all when expanded
    const productsToList = useMemo(() => {
        return showAllRetailers ? allProductsToList : allProductsToList.slice(0, 3);
    }, [allProductsToList, showAllRetailers]);
    const extraRetailerCount = allProductsToList.length - 3;
    const getReviewRatingValue = () => {
        const formatValue = (value) => {
            return Math.round(value * 10) / 10;
        };
        if (product.reviewsRating && product.reviewsRating > 0) {
            return formatValue(product.reviewsRating);
        }
        if (product.reviewsAverageRating && product.reviewsAverageRating > 0) {
            return formatValue(product.reviewsAverageRating);
        }
        return 0;
    };
    const getBrandclubPDPUrl = (stacklineSku) => {
        return `product-title/p/${stacklineSku}/product`;
    };
    const handleTitleClick = (e) => {
        e.preventDefault();
        const pdpUrl = getBrandclubPDPUrl(product.stacklineSku);
        window.open(pdpUrl, '_blank');
    };
    const handleBuyClick = (selectedProduct) => __awaiter(void 0, void 0, void 0, function* () {
        const eligibility = selectedProduct.checkoutEligibility;
        // If no cart callback provided, just open the retailer page
        if (!onAddToCart) {
            handleOpenRetailerProductPageClick(selectedProduct);
            return;
        }
        if (isServerSideCheckoutEligible(eligibility) && !isAddingToBag) {
            setIsAddingToBag(true);
            try {
                yield onAddToCart(selectedProduct);
                onOpenCart === null || onOpenCart === void 0 ? void 0 : onOpenCart();
            }
            catch (error) {
                console.error('Error adding to bag:', error);
            }
            finally {
                setIsAddingToBag(false);
            }
        }
    });
    const handleOpenRetailerProductPageClick = (selectedProduct) => {
        if (selectedProduct.retailerProductPageUrl) {
            window.open(selectedProduct.retailerProductPageUrl, '_blank');
        }
    };
    const reviewRating = getReviewRatingValue();
    const reviewCount = product.reviewsCount || 0;
    const description = product.description || '';
    const hasDescription = description.trim().length > 0;
    const descriptionLength = description.length;
    // Determine if we should show the details section (if description or bullets exist)
    const hasDetails = hasDescription || featureBullets.length > 0;
    // Show first 2 bullets when collapsed
    const displayBullets = showDetails ? featureBullets : featureBullets.slice(0, 2);
    return (React__default.createElement(Card, { className: "tw:max-w-4xl tw:w-full tw:my-2 tw:overflow-hidden tw:py-0 tw:border-0 tw:shadow-none" },
        React__default.createElement("div", { className: "tw:grid tw:grid-cols-1 featured-product-grid tw:gap-0" },
            images.length > 0 && React__default.createElement(ProductImageCarousel, { images: images, productTitle: product.title }),
            React__default.createElement(CardContent, { className: "tw:flex tw:flex-col tw:gap-5 tw:p-4 md:tw:p-5 md:tw:pt-5" },
                React__default.createElement("div", { className: "tw:flex tw:flex-col tw:gap-1.5" },
                    React__default.createElement("a", { href: `/p/${product.stacklineSku}/product`, onClick: handleTitleClick, target: "_blank", rel: "noopener noreferrer", className: "tw:text-base tw:no-underline tw:cursor-pointer tw:line-clamp-2 tw:leading-snug tw:hover:underline tw:transition-colors" },
                        React__default.createElement("h2", { className: "product-title tw:text-foreground" }, product.title)),
                    React__default.createElement("div", { className: "tw:flex tw:items-center tw:gap-3 tw:flex-wrap" }, reviewRating > 0 && (React__default.createElement("div", { className: "tw:flex tw:items-center tw:gap-1 tw:text-sm tw:font-medium" },
                        React__default.createElement(Star, { className: "tw:size-3.5 tw:fill-[#ed792f] tw:text-[#ed792f]" }),
                        React__default.createElement("span", { className: "tw:text-muted-foreground" }, reviewRating),
                        reviewCount > 0 && (React__default.createElement("span", { className: "tw:text-muted-foreground" },
                            "(",
                            numeral(reviewCount).format(`0,0`),
                            ")")))))),
                React__default.createElement("div", { className: "tw:my-2" },
                    React__default.createElement(ConciergeProductPrice, { productsToList: productsToList, onBuyClick: handleBuyClick, onOpenRetailerProductPageClick: handleOpenRetailerProductPageClick }),
                    extraRetailerCount > 0 && (React__default.createElement(Button, { variant: "ghost", size: "sm", onClick: () => setShowAllRetailers(!showAllRetailers), className: "tw:w-full tw:h-auto tw:text-sm tw:text-muted-foreground tw:px-0 tw:py-2 tw:justify-center tw:mt-1 tw:border-0 tw:shadow-none hover:tw:bg-transparent" }, showAllRetailers ? (React__default.createElement(React__default.Fragment, null,
                        React__default.createElement(ChevronUp, { className: "tw:size-3 tw:mr-1.5" }),
                        "Show less")) : (React__default.createElement(React__default.Fragment, null,
                        "Available at ",
                        extraRetailerCount,
                        " more retailer",
                        extraRetailerCount > 1 ? 's' : '',
                        React__default.createElement(ArrowRight, { className: "tw:size-3 tw:ml-1.5" })))))),
                hasDetails && (React__default.createElement("div", { className: "tw:flex tw:flex-col tw:gap-4 tw:text-sm tw:text-muted-foreground" }, showDetails ? (React__default.createElement(React__default.Fragment, null,
                    hasDescription && (React__default.createElement("div", { className: "tw:leading-relaxed", dangerouslySetInnerHTML: { __html: description } })),
                    featureBullets.length > 0 && (React__default.createElement("ul", { className: "tw:m-0 tw:list-none tw:flex tw:flex-col tw:gap-1" }, featureBullets.map((bullet, idx) => (React__default.createElement("li", { key: idx, className: "tw:leading-snug tw:flex tw:items-start tw:gap-2" },
                        React__default.createElement("span", { className: "tw:text-primary tw:mt-1 tw:shrink-0 tw:font-bold" }, "\u2022"),
                        React__default.createElement("span", null, bullet)))))),
                    (descriptionLength > 150 || featureBullets.length > 2) && (React__default.createElement("button", { onClick: () => setShowDetails(false), className: "tw:w-fit tw:h-auto tw:text-muted-foreground !tw:px-0 !tw:pl-0 !tw:pr-0 tw:py-0.5 tw:-mt-0.5 tw-flex tw-flex-row tw-items-center tw-justify-center", style: {
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                        } },
                        React__default.createElement(ChevronUp, { className: "tw:size-3 tw:mr-1" }),
                        "Show less")))) : (React__default.createElement(React__default.Fragment, null,
                    hasDescription && (React__default.createElement("div", { className: "tw:leading-relaxed tw:line-clamp-2", dangerouslySetInnerHTML: { __html: description } })),
                    featureBullets.length > 0 && (React__default.createElement("ul", { className: "tw:m-0 tw:list-none tw:flex tw:flex-col tw:gap-1" }, displayBullets.map((bullet, idx) => (React__default.createElement("li", { key: idx, className: "tw:tw:leading-snug tw:flex tw:items-start tw:gap-2" },
                        React__default.createElement("span", { className: "tw:mt-1 tw:shrink-0 tw:font-bold" }, "\u2022"),
                        React__default.createElement("span", { className: "tw:line-clamp-1" }, bullet)))))),
                    (descriptionLength > 150 || featureBullets.length > 2) && (React__default.createElement("button", { onClick: () => setShowDetails(true), className: "tw:w-fit tw:h-auto tw:text-muted-foreground tw:items-center tw:justify-center tw:flex tw:flex-row tw:gap-2" },
                        React__default.createElement(ChevronDown, { className: "tw:size-4" }),
                        "Show more"))))))))));
};

export { FeaturedProductWidget };
//# sourceMappingURL=FeaturedProductWidget.js.map
